import { useCallback, useMemo, useState } from 'react';
import { Epg, Layout } from 'planby-mit';
import { ChannelItem } from './channel-item.tsx';
import { EpgItem } from './epg-item.tsx';
import { Timeline } from './timeline.tsx';
import { useEpgResources } from './use-epg-resources.ts';
import { ClientOnly } from 'remix-utils/client-only';
import { ErrorBoundary } from 'react-error-boundary';
import { EPGLoadingSkeleton } from '../cell-components/skeletons.tsx';
import { type Feed } from '~/services/layout/index.ts';
import { EPGDateSelector } from './epg-days.tsx';
import { useMockEpg } from './use-mock-epg.ts';
import { getIsoDateString, getNormalizedEpgDateTime } from './utils.ts';
import clsx from 'clsx';

export default function EPG({
  uiComponent,
  feed,
}: {
  uiComponent: any;
  feed: Feed;
}): JSX.Element {
  const { isMock, mockDate } = useMockEpg();

  const [currentTime, setCurrentTime] = useState<string>(
    isMock ? getIsoDateString(new Date(mockDate)) : getIsoDateString()
  );

  const todayIso: string = useMemo(() => {
    const baseIso: string = isMock
      ? getIsoDateString(new Date(mockDate))
      : getIsoDateString();

    return getNormalizedEpgDateTime({ isoDate: baseIso });
  }, [isMock, mockDate]);

  const itemHeight: number = uiComponent?.styles?.channel_item_height || 80;
  const channelSidebarWidth: number =
    uiComponent?.styles?.channel_sidebar_width || 100;

  const {
    getEpgProps,
    getLayoutProps,
    isLoading,
    latestDate,
    timeSlotInterval,
  } = useEpgResources({
    uiComponent,
    feed,
    itemHeight,
    channelSidebarWidth,
    currentTime,
    setCurrentTime,
  });

  const handleDateChange = useCallback((isoDate: string) => {
    setCurrentTime(isoDate);
  }, []);

  const numberOfChannels: number = feed?.entry?.length || 0;
  const epgLoadingSkeleton: JSX.Element = (
    <EPGLoadingSkeleton
      loadingSkeletonItemHeight={itemHeight}
      channelSidebarWidth={channelSidebarWidth}
      numChannels={numberOfChannels}
      uiComponentId={uiComponent.id}
    />
  );

  const dayFilterSwitch: boolean = !!uiComponent?.styles?.day_filter_switch;

  const renderEpgProgram = useCallback(
    ({ program, ...rest }: { program: any }) => (
      <EpgItem
        isRTL={false}
        isBaseTimeFormat={false}
        key={program.data.id}
        program={program}
        uiComponent={uiComponent}
        {...rest}
      />
    ),
    [uiComponent]
  );

  const layoutProps = useMemo(() => getLayoutProps(), [getLayoutProps]);
  const epgProps = useMemo(() => getEpgProps(), [getEpgProps]);

  const programs = layoutProps.programs || [];

  if (numberOfChannels === 0) return <></>;

  if (isLoading || programs.length === 0) return epgLoadingSkeleton;

  const coreComponentClasses = `component-${uiComponent.id} component-${uiComponent.id}-epg`;

  return (
    <ClientOnly fallback={epgLoadingSkeleton}>
      {() => (
        <ErrorBoundary fallback={<></>}>
          {dayFilterSwitch && (
            <div
              className={clsx(
                coreComponentClasses,
                `mb-day_filter_margin_bottom ml-day_filter_margin_left mr-day_filter_margin_right mt-day_filter_margin_top flex items-center justify-center`
              )}
            >
              <EPGDateSelector
                onDateChange={handleDateChange}
                latestDate={latestDate}
                todayIso={todayIso}
              />
            </div>
          )}
          <div
            className={clsx(coreComponentClasses, `component-classes`)}
            data-testid={`epg-container-${uiComponent.id}`}
          >
            <Epg {...epgProps}>
              <Layout
                {...layoutProps}
                renderProgram={renderEpgProgram}
                renderChannel={({ channel }) => (
                  <ChannelItem key={channel.uuid} channel={channel} />
                )}
                renderTimeline={(props) => (
                  <Timeline
                    {...props}
                    startDate={currentTime}
                    timeSlotInterval={timeSlotInterval}
                  />
                )}
              />
            </Epg>
          </div>
        </ErrorBoundary>
      )}
    </ClientOnly>
  );
}
